import type {
  AiSpreadsheetOperationResult,
  IAiSpreadsheetPasteEdit,
} from "./types";

export const WORKBOOK_CELL_EDIT_BATCH_WINDOW_MS = 75;

export interface WorkbookCellEditBatchMetrics {
  batchSize: number;
  queueDurationMs: number;
  operationDurationMs: number;
  succeeded: boolean;
}

interface QueuedEditWaiter {
  resolve: (result: WorkbookCellEditBatchResult) => void;
  reject: (error: unknown) => void;
}

export interface WorkbookCellEditBatchInfo {
  id: string;
  editCount: number;
  requestCount: number;
}

export interface WorkbookCellEditBatchResult {
  operation: AiSpreadsheetOperationResult;
  batch: WorkbookCellEditBatchInfo;
}

export class WorkbookCellEditBatchError extends Error {
  constructor(
    public cause: unknown,
    public batch: WorkbookCellEditBatchInfo,
  ) {
    super(
      cause instanceof Error ? cause.message : "Workbook cell edits failed",
    );
    this.name = "WorkbookCellEditBatchError";
  }
}

interface PendingCellEditBatch {
  id: string;
  sheetId: string;
  edits: Map<string, IAiSpreadsheetPasteEdit>;
  waiters: QueuedEditWaiter[];
  queuedAt: number;
  timer: ReturnType<typeof globalThis.setTimeout> | null;
  execute: (
    sheetId: string,
    edits: IAiSpreadsheetPasteEdit[],
  ) => Promise<AiSpreadsheetOperationResult>;
  report?: (metrics: WorkbookCellEditBatchMetrics) => void;
  onError?: (error: WorkbookCellEditBatchError) => void;
}

interface WorkbookCellEditBatchState {
  active: Promise<void> | null;
  pending: PendingCellEditBatch | null;
  drainWaiters: Array<{
    resolve: () => void;
    reject?: (error: WorkbookCellEditBatchError) => void;
    error?: WorkbookCellEditBatchError;
  }>;
}

const batches = new Map<string, WorkbookCellEditBatchState>();

const workbookKey = (businessId: string, runId: string) =>
  `${businessId}:${runId}`;

const createState = (): WorkbookCellEditBatchState => ({
  active: null,
  pending: null,
  drainWaiters: [],
});

const resolveDrainWaiters = (state: WorkbookCellEditBatchState) => {
  if (state.active || state.pending) return;
  state.drainWaiters.splice(0).forEach((waiter) => {
    if (waiter.reject && waiter.error) waiter.reject(waiter.error);
    else waiter.resolve();
  });
};

const reportBatch = (
  batch: PendingCellEditBatch,
  metrics: WorkbookCellEditBatchMetrics,
) => {
  try {
    batch.report?.(metrics);
  } catch {
    // Measurement must never change edit delivery or queue progress.
  }
};

const reportBatchError = (
  batch: PendingCellEditBatch,
  error: WorkbookCellEditBatchError,
) => {
  try {
    batch.onError?.(error);
  } catch {
    // Error reporting must never change edit delivery or queue progress.
  }
};

const dispatchPendingBatch = (
  key: string,
  state: WorkbookCellEditBatchState,
) => {
  if (state.active || !state.pending) return;

  const batch = state.pending;
  state.pending = null;
  if (batch.timer) globalThis.clearTimeout(batch.timer);

  const edits = Array.from(batch.edits.values());
  const batchInfo = {
    id: batch.id,
    editCount: edits.length,
    requestCount: batch.waiters.length,
  };
  const operationStartedAt = performance.now();
  state.active = batch
    .execute(batch.sheetId, edits)
    .then(
      (result) => {
        const batchResult = { operation: result, batch: batchInfo };
        batch.waiters.forEach(({ resolve }) => resolve(batchResult));
        reportBatch(batch, {
          batchSize: edits.length,
          queueDurationMs: operationStartedAt - batch.queuedAt,
          operationDurationMs: performance.now() - operationStartedAt,
          succeeded: true,
        });
      },
      (error: unknown) => {
        const batchError = new WorkbookCellEditBatchError(error, batchInfo);
        state.drainWaiters.forEach((waiter) => {
          if (waiter.reject) waiter.error ??= batchError;
        });
        reportBatchError(batch, batchError);
        batch.waiters.forEach(({ reject }) => reject(batchError));
        reportBatch(batch, {
          batchSize: edits.length,
          queueDurationMs: operationStartedAt - batch.queuedAt,
          operationDurationMs: performance.now() - operationStartedAt,
          succeeded: false,
        });
      },
    )
    .finally(() => {
      state.active = null;
      if (state.pending) {
        dispatchPendingBatch(key, state);
        return;
      }

      resolveDrainWaiters(state);
      if (batches.get(key) === state) batches.delete(key);
    });
};

export const enqueueWorkbookCellEdit = ({
  businessId,
  runId,
  sheetId,
  edit,
  execute,
  report,
  onError,
}: {
  businessId: string;
  runId: string;
  sheetId: string;
  edit: IAiSpreadsheetPasteEdit;
  execute: PendingCellEditBatch["execute"];
  report?: PendingCellEditBatch["report"];
  onError?: PendingCellEditBatch["onError"];
}): Promise<WorkbookCellEditBatchResult> => {
  const key = workbookKey(businessId, runId);
  const state = batches.get(key) ?? createState();
  batches.set(key, state);

  if (state.pending && state.pending.sheetId !== sheetId) {
    return flushWorkbookCellEdits(businessId, runId).then(() =>
      enqueueWorkbookCellEdit({
        businessId,
        runId,
        sheetId,
        edit,
        execute,
        report,
        onError,
      }),
    );
  }

  const result = new Promise<WorkbookCellEditBatchResult>((resolve, reject) => {
    if (!state.pending) {
      state.pending = {
        id: crypto.randomUUID(),
        sheetId,
        edits: new Map(),
        waiters: [],
        queuedAt: performance.now(),
        timer: null,
        execute,
        report,
        onError,
      };
    }

    state.pending.edits.set(edit.cell_id, edit);
    state.pending.waiters.push({ resolve, reject });
  });

  if (!state.active && state.pending && !state.pending.timer) {
    state.pending.timer = globalThis.setTimeout(
      () => dispatchPendingBatch(key, state),
      WORKBOOK_CELL_EDIT_BATCH_WINDOW_MS,
    );
  }

  return result;
};

export const flushWorkbookCellEdits = (
  businessId: string,
  runId: string,
  { rejectOnError = false }: { rejectOnError?: boolean } = {},
): Promise<void> => {
  const key = workbookKey(businessId, runId);
  const state = batches.get(key);
  if (!state) return Promise.resolve();

  const drained = new Promise<void>((resolve, reject) => {
    state.drainWaiters.push({ resolve, ...(rejectOnError ? { reject } : {}) });
  });
  dispatchPendingBatch(key, state);
  resolveDrainWaiters(state);
  return drained;
};

export const hasPendingWorkbookCellEdits = (
  businessId: string,
  runId: string,
) => !!batches.get(workbookKey(businessId, runId))?.pending;

export const cancelWorkbookCellEdits = (businessId: string, runId: string) => {
  const key = workbookKey(businessId, runId);
  const state = batches.get(key);
  if (!state) return;

  batches.delete(key);
  if (state.pending?.timer) globalThis.clearTimeout(state.pending.timer);
  const error = new DOMException(
    "Workbook cell edit batch stopped",
    "AbortError",
  );
  if (state.pending) {
    const batchError = new WorkbookCellEditBatchError(error, {
      id: state.pending.id,
      editCount: state.pending.edits.size,
      requestCount: state.pending.waiters.length,
    });
    state.drainWaiters.forEach((waiter) => {
      if (waiter.reject) waiter.error ??= batchError;
    });
    state.pending.waiters.forEach(({ reject }) => reject(batchError));
  }
  state.pending = null;
  resolveDrainWaiters(state);
};
